import { createReducer, createAction } from '@reduxjs/toolkit';
import moment from 'moment';

import { backendCall, createApiCall } from '@reducers/utils';
import type { RootState, AppThunk } from '@store/types';
import type {
  BlobStorageState,
  SaveBlobStoragePayload,
  GenerateImageURLParams,
  GenerateNCFileURLParams,
} from './types';

// Re-export types for consumers
export type {
  BlobStorageState,
  SaveBlobStoragePayload,
  GenerateImageURLParams,
  GenerateNCFileURLParams,
  StorageAccessResponse,
  BlobFolder,
} from './types';

// === Initial State ===

const initState: BlobStorageState = {
  loaded: false,
  blobBaseUrl: null,
  blobContainer: null,
  sasToken: null,
  sasExpiredAt: null,
  folders: [],
  updating: false,
};

// === Selectors ===

export const selectBlobStorage = (state: RootState) => state.blobStorage;
export const selectBlobBaseUrl = (state: RootState) => state.blobStorage.blobBaseUrl;
export const selectSasToken = (state: RootState) => state.blobStorage.sasToken;
export const selectBlobFolders = (state: RootState) => state.blobStorage.folders;

// === Actions ===

export const save = createAction<SaveBlobStoragePayload>('blobStorage/save');
export const setUpdating = createAction<boolean>('blobStorage/setUpdating');
export const clear = createAction('clear');

// === Helper Functions ===

const delay = (delayInMs: number): Promise<number> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(2);
    }, delayInMs);
  });
};

// === Thunks ===

const startAppTime = moment();

export const getBlobStorage = (): AppThunk<Promise<BlobStorageState>> => async (dispatch, getState) => {
  const {
    blobStorage: { sasExpiredAt, updating },
    blobStorage,
  } = getState();

  const startSeconds = moment().diff(startAppTime, 'seconds');

  if (startSeconds < 1) {
    return blobStorage;
  }

  const isExpired = sasExpiredAt ? moment().add(3, 'minute').isAfter(sasExpiredAt) : true;

  if (!isExpired || updating) {
    return blobStorage;
  }

  console.log('Updating blob storage access...');
  dispatch(setUpdating(true));

  const response = await dispatch(
    backendCall(
      createApiCall((api) => api.blobStorage.get, {
        onSuccess: (data) => {
          const info: SaveBlobStoragePayload = {
            ...data,
            sasExpiredAt: moment().add(data.sasTokenLifespanMinutes, 'minutes'),
          };
          dispatch(save(info));
          dispatch(setUpdating(false));
          return info;
        },
        onError: (error) => {
          console.log(error, 'Error load blob config');
        },
      })
    )
  );

  return response as unknown as BlobStorageState;
};

export const generateImageURL =
  (data: GenerateImageURLParams): AppThunk<Promise<string>> =>
  async (dispatch, getState) => {
    await dispatch(getBlobStorage());
    const {
      blobStorage: { blobBaseUrl, blobContainer, sasToken, folders, updating },
    } = getState();

    if (!blobBaseUrl || updating) {
      await delay(1000);
      return await dispatch(generateImageURL(data));
    }

    const { size, cropped = true, page = false, extension = false, item, download, additionalParams } = data;

    const fileFolder = folders?.find((f) => f.folderType === 'File')?.folderName;
    const imageFolder = folders?.find((f) => f.folderType === 'Image')?.folderName;
    const croppedFolder = folders?.find((f) => f.folderType === 'Cropped')?.folderName;
    const filePath = fileFolder || 'file/';
    const imagePath = imageFolder || 'image/';
    const croppedPath = croppedFolder || 'cropped/';

    if (download) {
      return `${blobBaseUrl}/${blobContainer}/${filePath}${item.imageHash ?? item.contentHash}_${item.fileName}?${sasToken}`;
    }

    const path = cropped ? croppedPath : imagePath;
    const ext = extension ? extension : item.extensionDerivative || item.extensionOriginal;

    const baseUrl = `${blobBaseUrl}/${blobContainer}/${path}${item.imageHash ?? item.contentHash}${`_${page || 1}`}${size}${
      cropped ? '_cropped' : ''
    }.${ext}?${sasToken}`;

    // Add additional params if provided (for crop uniqueness)
    if (additionalParams) {
      return `${baseUrl}&${additionalParams}`;
    }

    return baseUrl;
  };

export const generateNCFileURL =
  (data: GenerateNCFileURLParams): AppThunk<Promise<string>> =>
  async (dispatch, getState) => {
    await dispatch(getBlobStorage());

    const {
      blobStorage: { blobBaseUrl, blobContainer, sasToken, folders, updating },
    } = getState();

    if (!blobBaseUrl || updating) {
      await delay(1000);
      return await dispatch(generateNCFileURL(data));
    }

    console.log(data, 'datadata');
    const { fullFile, ncFileContentHash } = data;

    const folder = fullFile ? 'NcFile' : 'NcSnapshot';

    const filePath = (folders.find((f) => f.folderType === folder) || { folderName: '' }).folderName;

    const hash = typeof ncFileContentHash === 'string' ? ncFileContentHash : ncFileContentHash?.fileContentHash;

    return `${blobBaseUrl}/${blobContainer}/${filePath}${hash}?${sasToken}`;
  };

export const getSasToken = (): AppThunk<Promise<string | null>> => async (dispatch, getState) => {
  await dispatch(getBlobStorage());

  const {
    blobStorage: { blobBaseUrl, sasToken, updating },
  } = getState();

  if (!blobBaseUrl || updating) {
    await delay(1000);
    return await dispatch(getSasToken());
  }

  return sasToken;
};

// === Reducer ===

export default createReducer<BlobStorageState>(initState, (builder) => {
  builder
    .addCase(save, (state, action) => ({
      ...state,
      loaded: true,
      ...action.payload,
    }))
    .addCase(setUpdating, (state, action) => ({
      ...state,
      updating: action.payload,
    }))
    .addCase(clear, () => initState);
});
